import { useCallback, useEffect, useState } from "react";
import { errorMessage, request } from "../lib/api";
import { track } from "../lib/events";
import type { PDDayDetail, RosterEntry, RosterResponse } from "../types";

// Two facilitators can share a session, so a running one is polled to pick
// up the other facilitator's marks — mirrors the live progress screen.
const POLL_MS = 20000;

export interface AttendanceData {
  detail: PDDayDetail;
  roster: RosterEntry[];
}

export interface AttendanceState {
  data: AttendanceData | null;
  loading: boolean;
  error: string;
  reload: () => Promise<void>;
}

async function fetchAttendance(sessionID: string): Promise<AttendanceData> {
  const [detail, roster] = await Promise.all([
    request<PDDayDetail>(`/api/facilitator/pd-days/${sessionID}`),
    request<RosterResponse>(`/api/facilitator/pd-days/${sessionID}/roster`),
  ]);
  return { detail, roster: roster.roster };
}

export function useAttendance(sessionID: string): AttendanceState {
  const [state, setState] = useState<Omit<AttendanceState, "reload">>({
    data: null,
    loading: true,
    error: "",
  });

  const reload = useCallback(
    () =>
      fetchAttendance(sessionID)
        .then((data) => setState({ data, loading: false, error: "" }))
        .catch((cause) =>
          setState((current) => ({ ...current, loading: false, error: errorMessage(cause) }))
        ),
    [sessionID]
  );

  useEffect(() => {
    track("page_opened", { payload: { page: "attendance", session_id: sessionID } });
    fetchAttendance(sessionID)
      .then((data) => setState({ data, loading: false, error: "" }))
      .catch((cause) =>
        setState((current) => ({ ...current, loading: false, error: errorMessage(cause) }))
      );
  }, [sessionID]);

  useEffect(() => {
    if (state.data?.detail.status !== "in_progress") return undefined;
    const timer = setInterval(() => void reload(), POLL_MS);
    return () => clearInterval(timer);
  }, [state.data?.detail.status, reload]);

  return { ...state, reload };
}
